import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useGroupStore } from '@/stores/groupStore';
import { usePlayerStore } from '@/stores/playerStore';
import { GroupHeader } from '@/components/GroupHeader';
import { cn, formatDuration, formatRelativeTime, getProfileColor } from '@/lib/utils';
import type { GroupPost } from '@/types';
import { likeBlahblah, unlikeBlahblah } from '@/api/blahblahs';

export function GroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const {
    currentGroup,
    posts,
    pinnedPosts,
    isLoadingDetail,
    isLoadingPosts,
    isLoadingMorePosts,
    postsHasMore,
    error,
    loadGroupDetail,
    loadGroupFeed,
    loadMorePosts,
    joinGroup,
    leaveGroup,
    deletePost,
    pinPost,
    unpinPost,
    resetDetail,
    isNotificationsMuted,
    muteGroupNotifications,
    unmuteGroupNotifications,
  } = useGroupStore();

  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    if (groupId) {
      resetDetail();
      loadGroupDetail(groupId);
    }
    return () => resetDetail();
  }, [groupId]);

  // Load feed once group is loaded and user is a member (including muted members)
  useEffect(() => {
    if (currentGroup && currentGroup.myRole &&
        (currentGroup.myStatus === 'active' || currentGroup.myStatus === 'muted') && groupId) {
      loadGroupFeed(groupId);
    }
  }, [currentGroup?.id, currentGroup?.myRole]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && postsHasMore && !isLoadingMorePosts && groupId) {
      loadMorePosts(groupId);
    }
  };

  if (isLoadingDetail) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !currentGroup) {
    return (
      <div className="text-center py-12 px-4">
        <p className="text-red-500">{error || 'Group not found'}</p>
        <Link to="/groups" className="mt-3 text-sm text-primary hover:underline inline-block">
          Back to Groups
        </Link>
      </div>
    );
  }

  const isMember = currentGroup.myRole != null &&
    (currentGroup.myStatus === 'active' || currentGroup.myStatus === 'muted');
  const canManage = currentGroup.myRole === 'owner' || currentGroup.myRole === 'moderator';
  const canPost = isMember && currentGroup.myStatus !== 'muted';

  const handleJoin = async () => {
    if (!groupId) return;
    setJoinError(null);
    try {
      await joinGroup(groupId);
    } catch (err: any) {
      setJoinError(err.message || 'Failed to join group');
    }
  };

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Back link */}
      <div className="px-4 py-2">
        <Link to="/groups" className="text-sm text-muted-foreground hover:text-primary transition-colors">
          &larr; Groups
        </Link>
      </div>

      {/* Group header */}
      <GroupHeader
        group={currentGroup}
        onJoin={handleJoin}
        onLeave={() => groupId && leaveGroup(groupId)}
        isNotificationsMuted={isNotificationsMuted}
        onMuteNotifications={() => groupId && muteGroupNotifications(groupId)}
        onUnmuteNotifications={() => groupId && unmuteGroupNotifications(groupId)}
      />

      {/* Join error message */}
      {joinError && (
        <div className="mx-4 mt-2 p-3 bg-destructive/10 text-destructive text-sm rounded-lg">
          {joinError}
        </div>
      )}

      {/* Record for group button */}
      {canPost && (
        <div className="px-4 py-3">
          <button
            onClick={() => navigate('/record', { state: { groupId, groupName: currentGroup.name } })}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            <RecordMicIcon className="w-5 h-5" />
            Record for {currentGroup.name}
          </button>
        </div>
      )}

      {/* Non-member message */}
      {!isMember && (
        <div className="text-center py-12 px-4">
          <p className="text-muted-foreground">Join this group to see posts</p>
        </div>
      )}

      {/* Pinned posts */}
      {isMember && pinnedPosts.length > 0 && (
        <div className="border-b border-border">
          <div className="px-4 py-2">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <PinIcon className="w-3.5 h-3.5" />
              Pinned
            </h3>
          </div>
          {pinnedPosts.map((post) => (
            <GroupPostItem
              key={post.id}
              post={post}
              canManage={canManage}
              groupId={groupId!}
              onDelete={deletePost}
              onPin={pinPost}
              onUnpin={unpinPost}
              onPostUpdated={(postId, updates) => {
                useGroupStore.setState((s) => ({
                  pinnedPosts: s.pinnedPosts.map((p) =>
                    p.id === postId ? { ...p, blahblah: p.blahblah ? { ...p.blahblah, ...updates } : p.blahblah } : p
                  ),
                }));
              }}
            />
          ))}
        </div>
      )}

      {/* Posts loading */}
      {isMember && isLoadingPosts && posts.length === 0 && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      )}

      {/* Posts empty */}
      {isMember && !isLoadingPosts && posts.length === 0 && pinnedPosts.length === 0 && (
        <div className="text-center py-12 px-4">
          <p className="text-muted-foreground">No posts yet. Be the first to share!</p>
        </div>
      )}

      {/* Posts feed */}
      {isMember && posts.map((post) => (
        <GroupPostItem
          key={post.id}
          post={post}
          canManage={canManage}
          groupId={groupId!}
          onDelete={deletePost}
          onPin={pinPost}
          onUnpin={unpinPost}
          onPostUpdated={(postId, updates) => {
            useGroupStore.setState((s) => ({
              posts: s.posts.map((p) =>
                p.id === postId ? { ...p, blahblah: p.blahblah ? { ...p.blahblah, ...updates } : p.blahblah } : p
              ),
            }));
          }}
        />
      ))}

      {/* Loading more */}
      {isLoadingMorePosts && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      )}

      {/* End of feed */}
      {isMember && !postsHasMore && posts.length > 0 && (
        <p className="text-center text-muted-foreground text-sm py-4">
          No more posts
        </p>
      )}
    </div>
  );
}

function GroupPostItem({
  post,
  canManage,
  groupId,
  onDelete,
  onPin,
  onUnpin,
  onPostUpdated,
}: {
  post: GroupPost;
  canManage: boolean;
  groupId: string;
  onDelete: (groupId: string, postId: string) => void;
  onPin: (groupId: string, postId: string) => void;
  onUnpin: (groupId: string, postId: string) => void;
  onPostUpdated?: (postId: string, updates: { isLiked: boolean; likesCount: number }) => void;
}) {
  const { currentId, isPlaying, progress, play, pause } = usePlayerStore();
  const [isLiking, setIsLiking] = useState(false);
  const blahblah = post.blahblah;
  if (!blahblah) return null;

  const isCurrentlyPlaying = currentId === blahblah.id && isPlaying;
  const playbackProgress = currentId === blahblah.id ? progress : 0;
  const progressPercent = (playbackProgress / (blahblah.durationMs || 1)) * 100;

  const handlePlayPause = () => {
    if (isCurrentlyPlaying) {
      pause();
    } else {
      play(blahblah.id, blahblah.audioUrl);
    }
  };

  const handleLikeToggle = async () => {
    if (isLiking) return;
    setIsLiking(true);
    try {
      if (blahblah.isLiked) {
        const result = await unlikeBlahblah(blahblah.id);
        onPostUpdated?.(post.id, { isLiked: result.isLiked, likesCount: result.likesCount });
      } else {
        const result = await likeBlahblah(blahblah.id);
        onPostUpdated?.(post.id, { isLiked: result.isLiked, likesCount: result.likesCount });
      }
    } catch {
      // silently fail
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <article className="border-b border-border p-4 transition-colors hover:bg-muted/30">
      {/* User header */}
      <div className="flex items-start gap-3 mb-3">
        <Link to={`/profile/${blahblah.user?.id}`}>
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
            style={{ backgroundColor: getProfileColor(blahblah.user?.profileColor) }}
          >
            {(blahblah.user?.displayName || '?').charAt(0).toUpperCase()}
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/profile/${blahblah.user?.id}`}
              className="font-medium text-card-foreground hover:text-primary transition-colors text-sm"
            >
              {blahblah.user?.displayName || 'Unknown'}
            </Link>
            {post.isPinned && (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <PinIcon className="w-3 h-3" /> Pinned
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {formatRelativeTime(post.createdAt)}
          </p>
        </div>

        {/* Post actions */}
        {canManage && (
          <PostMenu
            post={post}
            groupId={groupId}
            onDelete={onDelete}
            onPin={onPin}
            onUnpin={onUnpin}
          />
        )}
      </div>

      {/* Audio player */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={handlePlayPause}
          className={cn(
            'w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-sm',
            isCurrentlyPlaying
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary text-primary hover:bg-secondary/80'
          )}
        >
          {isCurrentlyPlaying ? (
            <PauseIcon className="w-5 h-5" />
          ) : (
            <PlayIcon className="w-5 h-5 ml-0.5" />
          )}
        </button>
        <div className="flex-1">
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-100"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span>{formatDuration(playbackProgress)}</span>
            <span>{formatDuration(blahblah.durationMs)}</span>
          </div>
        </div>
      </div>

      {/* Tags */}
      {blahblah.tags && blahblah.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {blahblah.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-secondary text-secondary-foreground text-xs rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Transcript */}
      {blahblah.transcript && (
        <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
          {blahblah.transcript}
        </p>
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
        <button
          onClick={handleLikeToggle}
          disabled={isLiking}
          className={cn(
            'flex items-center gap-1 transition-colors',
            blahblah.isLiked ? 'text-red-500' : 'hover:text-red-500'
          )}
        >
          <HeartIcon className="w-4 h-4" filled={blahblah.isLiked} />
          {blahblah.likesCount}
        </button>
        <span>{blahblah.viewsCount} views</span>
        {blahblah.replyCount > 0 && (
          <Link to={`/blahblah/${blahblah.id}`} className="hover:text-primary transition-colors">
            {blahblah.replyCount} replies
          </Link>
        )}
      </div>
    </article>
  );
}

function PostMenu({
  post,
  groupId,
  onDelete,
  onPin,
  onUnpin,
}: {
  post: GroupPost;
  groupId: string;
  onDelete: (groupId: string, postId: string) => void;
  onPin: (groupId: string, postId: string) => void;
  onUnpin: (groupId: string, postId: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-full hover:bg-muted transition-colors"
      >
        <MoreHIcon className="w-4 h-4 text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 w-36 bg-card border border-border rounded-lg shadow-lg py-1">
            {post.isPinned ? (
              <button
                onClick={() => { onUnpin(groupId, post.id); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
              >
                Unpin
              </button>
            ) : (
              <button
                onClick={() => { onPin(groupId, post.id); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
              >
                Pin
              </button>
            )}
            <button
              onClick={() => { onDelete(groupId, post.id); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors"
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
    </svg>
  );
}

function MoreHIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

function HeartIcon({ className, filled }: { className?: string; filled?: boolean }) {
  return filled ? (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ) : (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function RecordMicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}
